import React, { useState, useEffect } from 'react';
import { useConsentStore } from '@/stores/consentStore';
import { Button } from '@/components/ui/button';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const ConsentBanner: React.FC = () => {
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const {
    jurisdiction,
    isConsentBannerOpen,
    setAllConsents,
    closeConsentBanner,
    openPreferencesModal,
    hasMadeChoice
  } = useConsentStore();

  if (!isClient || !isConsentBannerOpen || hasMadeChoice) {
    return null;
  }

  const handleAcceptAll = () => setAllConsents('granted');
  const handleRejectNonEssential = () => {
    useConsentStore.setState(prev => ({
        consent: { ...prev.consent, performance: 'denied', analytics: 'denied', advertising: 'denied' },
        hasMadeChoice: true, isConsentBannerOpen: false,
    }));
  };
  
  const handleManagePreferences = () => {
    openPreferencesModal();
  };

  let bannerText = "We use cookies and similar technologies to enhance your experience, analyze site traffic, and personalize content.";
  if (jurisdiction === 'EU') {
    bannerText += " You can accept all, reject non-essential cookies, or customize your preferences.";
  } else if (jurisdiction === 'CA') {
    bannerText += " You have the right to opt-out of the sale/sharing of your personal information via 'Manage Preferences'.";
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-2xl p-4 z-[100] animate-in slide-in-from-bottom-full duration-500">
      <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
        <div className="flex items-start space-x-3 flex-grow">
          <Cookie className="h-8 w-8 text-primary flex-shrink-0 mt-1 hidden sm:block" />
          <div>
            <h3 className="text-md font-semibold text-foreground">We Value Your Privacy</h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              {bannerText} Learn more in our <Link to="/privacy-policy" className="underline hover:text-primary">Privacy Policy</Link>.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto mt-3 md:mt-0 flex-shrink-0">
          <Button onClick={handleAcceptAll} size="sm" className="w-full sm:w-auto">Accept All</Button>
          {(jurisdiction === 'EU' || jurisdiction === 'CA') && 
            <Button onClick={handleManagePreferences} variant="outline" size="sm" className="w-full sm:w-auto">Manage Preferences</Button>
          }
          {jurisdiction === 'EU' && 
            <Button onClick={handleRejectNonEssential} variant="ghost" size="sm" className="w-full sm:w-auto">Reject Non-Essential</Button>
          }
          {jurisdiction !== 'EU' && jurisdiction !== 'CA' &&
             <Button onClick={closeConsentBanner} variant="outline" size="sm" className="w-full sm:w-auto">Dismiss</Button>
          }
        </div>
         <Button variant="ghost" size="icon" className="absolute top-1 right-1 md:hidden" onClick={closeConsentBanner}>
            <X className="h-5 w-5" />
            <span className="sr-only">Close consent banner</span>
        </Button>
      </div>
    </div>
  );
};

export default ConsentBanner;
